import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * Two-section unit dropdown used across the medicine dose block (scalar,
 * As-needed, per-slot). Replaces the earlier trio of shapes (free-text
 * input for 0-unit formulations, static pill for 1-unit, Select for N).
 *
 * Sections:
 *   RECOMMENDED — the formulation catalog's units[] for the currently
 *                 picked REFLD code.
 *   OTHER       — every distinct unit across the whole catalog, minus
 *                 the Recommended set. Alphabetical A→Z.
 *
 * The picker is controlled: `value` is the currently-selected unit
 * string; `onChange(unit)` fires on pick (and closes the popover).
 * Empty Recommended = the section header hides. Empty Other likewise.
 */
const UnitPicker = ({ value, recommended, other, onChange, disabled, ariaLabel }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const pick = (u) => {
    onChange(u);
    setOpen(false);
  };

  const rec = Array.isArray(recommended) ? recommended : [];
  const oth = Array.isArray(other) ? other : [];
  // Empty `value` always renders as "None" now that None is a first-class
  // pickable option — the earlier "unit" placeholder read as an unset
  // field, but a saved medicine that came back with an empty unit was
  // deliberately marked None and should read that way on reload.
  const placeholder = "None";

  return (
    <div className="med3-unit-picker" ref={boxRef}>
      <button
        type="button"
        className="med3-unit-trigger"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel || "Dose unit"}
        title={value || undefined}
      >
        <span className={`med3-unit-trigger-val${value ? "" : " is-placeholder"}`}>
          {value || placeholder}
        </span>
        <svg
          className="med3-unit-trigger-caret"
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden="true"
        >
          <path d="M1 1l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="med3-unit-menu" role="listbox" aria-label={ariaLabel || "Dose unit"}>
          {/* "None" is the first item in every menu — picking it clears
              the unit and downstream forms gray out the Qty box. Kept
              above the Recommended / Other sections so it's the first
              affordance a reviewer sees. */}
          <button
            type="button"
            role="option"
            aria-selected={!value}
            className={`med3-unit-opt${!value ? " is-on" : ""}`}
            onClick={() => pick("")}
            title="None"
          >
            <span className="med3-unit-opt-lbl">None</span>
            {!value && (
              <svg
                className="med3-unit-opt-check"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M2 7l3.5 3.5L12 3.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </button>
          {rec.length > 0 && (
            <>
              <div className="med3-unit-section">Recommended</div>
              {rec.map((u) => (
                <button
                  key={`r:${u}`}
                  type="button"
                  role="option"
                  aria-selected={value === u}
                  className={`med3-unit-opt${value === u ? " is-on" : ""}`}
                  onClick={() => pick(u)}
                  title={u}
                >
                  <span className="med3-unit-opt-lbl">{u}</span>
                  {value === u && (
                    <svg
                      className="med3-unit-opt-check"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M2 7l3.5 3.5L12 3.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ))}
            </>
          )}
          {oth.length > 0 && (
            <>
              <div className="med3-unit-section">Other</div>
              {oth.map((u) => (
                <button
                  key={`o:${u}`}
                  type="button"
                  role="option"
                  aria-selected={value === u}
                  className={`med3-unit-opt${value === u ? " is-on" : ""}`}
                  onClick={() => pick(u)}
                  title={u}
                >
                  <span className="med3-unit-opt-lbl">{u}</span>
                  {value === u && (
                    <svg
                      className="med3-unit-opt-check"
                      viewBox="0 0 14 14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M2 7l3.5 3.5L12 3.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ))}
            </>
          )}
          {rec.length === 0 && oth.length === 0 && (
            <div className="med3-unit-empty">No units available</div>
          )}
        </div>
      )}
    </div>
  );
};

UnitPicker.propTypes = {
  value: PropTypes.string,
  recommended: PropTypes.arrayOf(PropTypes.string),
  other: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default UnitPicker;
